import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/config";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";

export default function ContactForm({ defaultService = "", compact = false, testIdPrefix = "contact" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    service_interest: defaultService,
  });
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, service_interest: defaultService || f.service_interest }));
  }, [defaultService]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWa = () => {
    const lines = [
      "New enquiry from KK Associates website:",
      `Name: ${form.name}`,
      `Phone/WhatsApp: ${form.phone}`,
      `Email: ${form.email}`,
      form.service_interest ? `Service: ${form.service_interest}` : "",
      form.subject ? `Subject: ${form.subject}` : "",
      `Message: ${form.message}`,
    ].filter(Boolean);
    return waLink(lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone || form.phone.trim().length < 6) {
      setStatus({ loading: false, ok: false, err: "Phone / WhatsApp number is required." });
      return;
    }
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await api.post("/contact", form);
      // Open WhatsApp with pre-filled message so KK Associates gets an instant WhatsApp too
      window.open(buildWa(), "_blank", "noopener,noreferrer");
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "", service_interest: defaultService });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong.";
      setStatus({ loading: false, ok: false, err: msg });
    }
  };

  const input =
    "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none transition-colors";

  if (status.ok) {
    return (
      <div data-testid={`${testIdPrefix}-success`} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900">
        <div className="flex items-center gap-2 font-medium"><CheckCircle2 size={18} /> Thank you!</div>
        <p className="mt-2 text-sm">
          We've received your enquiry and a WhatsApp window has opened so you can hit send — our team replies faster on WhatsApp ({CONTACT.phone}).
        </p>
        <button onClick={() => setStatus({ loading: false, ok: false, err: "" })} className="mt-4 text-sm text-emerald-800 underline underline-offset-2">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`} className="grid grid-cols-1 gap-3">
      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-3`}>
        <input required data-testid={`${testIdPrefix}-name`} className={input} placeholder="Your name *" value={form.name} onChange={update("name")} />
        <input required type="email" data-testid={`${testIdPrefix}-email`} className={input} placeholder="Email address *" value={form.email} onChange={update("email")} />
      </div>
      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-3`}>
        <input required minLength={6} inputMode="tel" data-testid={`${testIdPrefix}-phone`} className={input} placeholder="Phone / WhatsApp number *" value={form.phone} onChange={update("phone")} />
        <select data-testid={`${testIdPrefix}-service`} className={input} value={form.service_interest} onChange={update("service_interest")}>
          <option value="">Select service (optional)</option>
          {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
        </select>
      </div>
      <input data-testid={`${testIdPrefix}-subject`} className={input} placeholder="Subject (optional)" value={form.subject} onChange={update("subject")} />
      <textarea required data-testid={`${testIdPrefix}-message`} rows={compact ? 3 : 5} className={input} placeholder="Tell us about your requirement… *" value={form.message} onChange={update("message")} />
      <p className="text-xs text-slate-500">
        On submit, your enquiry is saved <span className="text-slate-700">and</span> a WhatsApp window opens pre-filled to <span className="text-slate-700">{CONTACT.phone}</span> — just tap send.
      </p>
      {status.err && <div className="text-sm text-red-600" data-testid={`${testIdPrefix}-error`}>{status.err}</div>}
      <button
        type="submit"
        disabled={status.loading}
        data-testid={`${testIdPrefix}-submit`}
        className="btn-hover mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
      >
        {status.loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><MessageCircle size={16} /> Send & WhatsApp</>}
      </button>
    </form>
  );
}
